<template lang="pug">
.add-funds-callback
  // Loading State
  .state-container(v-if="isProcessing")
    .spinner
    p.message Processing payment...

  // Success/Failure State
  .state-container(v-else)
    // Icon
    .icon-container
      .icon.icon-success(v-if="isSuccess") ✓
      .icon.icon-error(v-else) ✗

    // Title
    h1.title {{ title }}

    // Message
    p.message {{ message }}

    // Countdown or Manual Action (Web)
    .action-container(v-if="source === 'web'")
      p.countdown Redirecting in {{ countdown }}...
      el-button.btn-return(@click="handleManualReturn") Return to Wallet

    // Mobile App Message
    .action-container(v-else)
      p.countdown Returning to app...
      p.manual-close(v-if="showManualClose") If the app doesn't open, you can close this window.
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, onMounted,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';

export default defineComponent({
  name: 'AddFunds',
  setup() {
    const router = useRouter();
    const route = useRoute();

    // State
    const isProcessing = ref(true);
    const isSuccess = ref(false);
    const amount = ref<string | null>(null);
    const source = ref('web');
    const countdown = ref(3);
    const showManualClose = ref(false);

    // Computed
    const title = computed(() => (isSuccess.value ? 'Payment Successful!' : 'Payment Failed'));

    const message = computed(() => {
      if (isSuccess.value && amount.value) {
        const amountNum = parseFloat(amount.value);
        return `$${amountNum.toFixed(2)} has been added to your wallet`;
      }
      return "We couldn't process your payment. Please try again.";
    });

    // Methods
    const processCallback = async () => {
      try {
        // Parse query parameters
        const action = route.query.action as string;
        const success = route.query.success === 'true';
        const amountParam = route.query.amount as string;
        const sourceParam = (route.query.source as string) || 'web';

        // Validate action
        if (action !== 'addToWallet') {
          redirectToWallet();
          return;
        }

        // Set state
        isSuccess.value = success;
        amount.value = amountParam;
        source.value = sourceParam;
        isProcessing.value = false;

        // Start countdown
        startCountdown();
      } catch (error) {
        console.error('Error processing payment callback:', error);
        isSuccess.value = false;
        isProcessing.value = false;
        startCountdown();
      }
    };

    const startCountdown = () => {
      const timer = setInterval(() => {
        countdown.value -= 1;
        if (countdown.value <= 0) {
          clearInterval(timer);
          redirectToWallet();
        }
      }, 1000);
    };

    const redirectToWallet = () => {
      if (source.value === 'app') {
        redirectToMobileApp();
      } else {
        router.push('/web/wallet/dashboard');
      }
    };

    const redirectToMobileApp = () => {
      const action = 'addToWallet';
      const success = isSuccess.value;
      const amountParam = amount.value || '0';

      // Determine environment and custom URL scheme
      const customUrl = getCustomUrlScheme(action, success, amountParam);

      // Attempt redirect
      window.location.href = customUrl;

      // Fallback: Show manual close message after 1 second
      setTimeout(() => {
        showManualClose.value = true;
      }, 1000);
    };

    const getCustomUrlScheme = (action: string, success: boolean, amountValue: string) => {
      // Determine scheme based on current domain
      const { hostname } = window.location;
      let scheme = 'com.spotlight.xyz://';

      if (hostname.includes('staging')) {
        scheme = 'com.spotlight.xyz.staging://';
      } else if (hostname.includes('dev') || hostname.includes('localhost')) {
        scheme = 'com.spotlight.xyz.dev://';
      }

      return `${scheme}app.spotlight.xyz?action=${action}&success=${success}&amount=${amountValue}`;
    };

    const handleManualReturn = () => {
      router.push('/web/wallet/dashboard');
    };

    // Lifecycle
    onMounted(() => {
      processCallback();
    });

    return {
      isProcessing,
      isSuccess,
      amount,
      title,
      message,
      countdown,
      source,
      showManualClose,
      handleManualReturn,
    };
  },
});
</script>

<style lang="scss" scoped>
.add-funds-callback {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #f5f5f5;
  padding: 20px;
}

.state-container {
  background: white;
  border-radius: 16px;
  padding: 48px 32px;
  max-width: 500px;
  width: 100%;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid #0d9488;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 24px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.icon-container {
  margin-bottom: 24px;
}

.icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  font-weight: bold;
  margin: 0 auto;
}

.icon-success {
  background: #E8F5E9;
  color: #4CAF50;
}

.icon-error {
  background: #FFEBEE;
  color: #F44336;
}

.title {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 16px;
  color: #080808;
}

.message {
  font-size: 16px;
  color: #474E47;
  margin-bottom: 32px;
  line-height: 1.5;
}

.action-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.countdown {
  font-size: 14px;
  color: #7F877F;
}

.btn-return {
  padding: 14px 32px;
  background: #0d9488;
  color: white;
  border: none;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #0d9488;
  }
}

.manual-close {
  font-size: 14px;
  color: #7F877F;
  margin-top: 8px;
}

@media (max-width: 768px) {
  .state-container {
    padding: 32px 24px;
  }

  .title {
    font-size: 24px;
  }

  .message {
    font-size: 14px;
  }
}
</style>
